import { Link } from "react-router-dom";
import { MapPin, Phone, ShieldCheck, Lock, Facebook, Instagram } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3h-3v12.2a2.6 2.6 0 1 1-2-2.53V9.6a5.6 5.6 0 1 0 5 5.56V8.9a6.9 6.9 0 0 0 4 1.28V7.16a3.9 3.9 0 0 1-4-4.16Z" />
  </svg>
);

const SOCIALS = [
  { label: "Facebook", href: "https://www.facebook.com/", Icon: Facebook, testId: "footer-facebook" },
  { label: "TikTok", href: "https://www.tiktok.com/", Icon: TikTokIcon, testId: "footer-tiktok" },
  { label: "Instagram", href: "https://www.instagram.com/", Icon: Instagram, testId: "footer-instagram" },
];

const Footer = () => {
  const { t } = useLanguage();
  return (
  <footer className="bg-[#1A2F24] text-[#FAF9F6]" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-12 gap-6 md:gap-12">
      <div className="col-span-12 md:col-span-5">
        <div className="flex items-center gap-3">
          <img src="/images/logo-icon.png" alt="Living Fruit Outreach tree emblem" className="h-12 w-12 object-contain" data-testid="footer-logo" />
          <span className="font-display text-2xl">Living Fruit Outreach Inc</span>
        </div>
        <p className="mt-6 max-w-sm text-sm leading-relaxed text-[#C4D7D1]">
          {t("foot_tag")}
        </p>
        <p className="mt-6 text-xs tracking-[0.15em] uppercase text-[#F4A261]" data-testid="footer-domain">livingfruitoutreach.com</p>
      </div>
      <div className="col-span-12 md:col-span-4">
        <h4 className="text-xs tracking-[0.25em] uppercase text-[#C4D7D1] mb-6">{t("foot_visit")}</h4>
        <p className="flex items-start gap-3 text-sm text-[#FAF9F6]/85" data-testid="footer-address">
          <MapPin size={16} className="mt-0.5 text-[#F4A261] shrink-0" />
          2886 Running Brook Cir<br />Kissimmee, Florida
        </p>
        <a href="tel:4076139157" data-testid="footer-phone" className="mt-4 flex items-center gap-3 text-sm text-[#FAF9F6]/85 hover:text-[#F4A261] transition-colors duration-300">
          <Phone size={16} className="text-[#F4A261]" /> 407-613-9157
        </a>
        <h4 className="text-xs tracking-[0.25em] uppercase text-[#C4D7D1] mt-8 mb-4">Follow Us</h4>
        <div className="flex items-center gap-3" data-testid="footer-socials">
          {SOCIALS.map(({ label, href, Icon, testId }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={testId}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-[#FAF9F6] transition-colors duration-300 hover:bg-[#F4A261] hover:text-white">
              <Icon size={18} />
            </a>
          ))}
        </div>
      </div>
      <div className="col-span-12 md:col-span-3">
        <h4 className="text-xs tracking-[0.25em] uppercase text-[#C4D7D1] mb-6">{t("foot_standing")}</h4>
        <p className="flex items-start gap-3 text-sm text-[#FAF9F6]/85" data-testid="footer-ein">
          <ShieldCheck size={16} className="mt-0.5 text-[#F4A261] shrink-0" />
          EIN 42-4118690
        </p>
        <p className="mt-4 text-sm text-[#FAF9F6]/85 leading-relaxed" data-testid="footer-charity-status">
          {t("foot_charity")}
        </p>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-[#C4D7D1]/70">© {new Date().getFullYear()} {t("foot_rights")}</p>
        <p className="font-display italic text-sm text-[#C4D7D1]">{t("foot_quote")}</p>
        <Link to="/admin/login" data-testid="footer-admin-link" className="inline-flex items-center gap-1.5 text-xs text-[#C4D7D1]/60 hover:text-[#F4A261] transition-colors duration-300">
          <Lock size={11} /> {t("foot_admin")}
        </Link>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
